import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, ShieldCheck } from 'lucide-react';

export const TwoFactorAuth = () => {
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    checkMFAStatus();
  }, []);

  const checkMFAStatus = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      
      const totpFactor = data?.totp.find(f => f.status === 'verified');
      setIsEnrolled(!!totpFactor);
    } catch (error) {
      console.error('Error checking MFA status:', error);
    }
  };

  const enrollMFA = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'ChessHatch 2FA',
      });

      if (error) throw error;

      setQrCode(data.totp.qr_code);
      setShowSetup(true);
      toast.success('Scan the QR code with your authenticator app');
    } catch (error: any) {
      toast.error(error.message || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    try {
      setLoading(true);
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totpFactor = factors?.totp[0];
      
      if (!totpFactor) {
        toast.error('No 2FA enrollment found');
        return;
      }

      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: totpFactor.id,
        code: verifyCode,
      });

      if (error) throw error;

      setIsEnrolled(true);
      setShowSetup(false);
      setQrCode(null);
      setVerifyCode('');
      toast.success('Two-factor authentication enabled successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const unenrollMFA = async () => {
    try {
      setLoading(true);
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totpFactor = factors?.totp.find(f => f.status === 'verified');
      
      if (!totpFactor) return;

      const { error } = await supabase.auth.mfa.unenroll({
        factorId: totpFactor.id,
      });

      if (error) throw error;

      setIsEnrolled(false);
      toast.success('Two-factor authentication disabled');
    } catch (error: any) {
      toast.error(error.message || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isEnrolled ? <ShieldCheck className="h-5 w-5 text-green-500" /> : <Shield className="h-5 w-5" />}
          Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Add an extra layer of security to your account
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!showSetup ? (
          <>
            {isEnrolled ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <ShieldCheck className="h-4 w-4" />
                  <span>Two-factor authentication is enabled</span>
                </div>
                <Button 
                  onClick={unenrollMFA} 
                  disabled={loading}
                  variant="destructive"
                >
                  {loading ? 'Processing...' : 'Disable 2FA'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Protect your account with two-factor authentication. You'll need an authenticator app like Google Authenticator or Authy.
                </p>
                <Button 
                  onClick={enrollMFA} 
                  disabled={loading}
                >
                  {loading ? 'Setting up...' : 'Enable 2FA'}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                1. Scan this QR code with your authenticator app:
              </p>
              {qrCode && (
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img src={qrCode} alt="2FA QR Code" className="max-w-[200px]" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="verify-code">2. Enter the 6-digit code from your app:</Label>
              <Input
                id="verify-code"
                type="text"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={verifyAndEnable} 
                disabled={loading || verifyCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify and Enable'}
              </Button>
              <Button 
                onClick={() => {
                  setShowSetup(false);
                  setQrCode(null);
                  setVerifyCode('');
                }}
                variant="outline"
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
